import Vue from 'vue';
import Router from 'vue-router';

import store from '@/store';

import * as Admin from '@/components/admin';
import App from '@/components/App';
import AppSignedOut from '@/components/AppSignedOut';
import SignIn from '@/components/SignIn';
import SignUp from '@/components/SignUp';
import ForgotPassword from '@/components/ForgotPassword';
import OtpVerification from '@/components/OtpVerification';
import UniversalLogin from '@/components/UniversalLogin';
import Sensor from '@/components/pages/Sensor';
import SensorList from '@/components/SensorList';
import StationPlaces from '@/components/pages/StationPlaces';
import StationPlaceEvents from '@/components/pages/StationPlaceEvents';
import StationPlaceRecentEvents from '@/components/pages/StationPlaceRecentEvents';
import StationPlaceFloorPlans from '@/components/pages/StationPlaceFloorPlans/StationPlaceFloorPlans';
import Activity from '@/components/pages/Activity';
import UserForm from '@/components/UserForm';
import Organization from '@/components/pages/Organization';
import InformationTab from '@/components/InformationTab';
import MirakunDevices from '@/components/pages/MirakunDevices';
import ActivityLogs from '@/components/pages/admin/ActivityLogs';
import Dashboard from '@/components/pages/Dashboard/Dashboard';
import RatTimeline from '@/components/pages/RatTimeline';
import RTypePlaces from '@/components/pages/RTypePlaces';
import PlaceFloorPlans from '@/components/pages/PlaceFloorPlans/PlaceFloorPlans';
import RTypeDetectionTrend from '@/components/pages/RTypeDetectionTrend';

Vue.use(Router);

export const buildRouter = (vuexStore, routerOptions) => {
  const router = new Router(routerOptions);

  const requiredPermissions = route => (
    route.matched.map(r => r.meta.requiresPermission).filter(r => r)
  );

  const currentUserHasRoutePermissions = (route) => {
    if (!vuexStore.getters.isSignedIn || !vuexStore.getters.currentUserRetrieved) {
      return false;
    }
    // 入れ子のルートが要求する権限はすべて満たす必要がある。
    // 1 つのルートに複数指定した場合は、そのいずれかを満たせばよい。
    return requiredPermissions(route).every(
      permission => [].concat(permission).some(p => vuexStore.getters.hasPermission(p)),
    );
  };

  const isAdminPage = route => route.matched.some(r => r.meta.adminOnly);
  // 管理画面のうち admin だけが使えるページ（代理店には開かない）
  const isAdminRolePage = route => route.matched.some(r => r.meta.adminRoleOnly);
  // 代理店は、admin 限定でない管理画面と、自分のアカウント画面にだけ入れる
  const isOpenToDistributors = route => (
    (isAdminPage(route) && !isAdminRolePage(route)) || route.matched.some(r => r.meta.openToDistributors)
  );

  router.beforeEach((to, _from, next) => {
    if (to.matched.some(r => r.meta.authRequired)) {
      if (!vuexStore.getters.isSignedIn) {
        next({ path: '/sign_in', query: { redirectTo: to.path } });
        return;
      }
      if (!currentUserHasRoutePermissions(to)) {
        next('/');
        return;
      }
      const currentUser = vuexStore.getters.getCurrentUser;
      if (currentUser.distributor) {
        if (!isOpenToDistributors(to)) {
          next('/admin');
          return;
        }
      } else if (isAdminPage(to) && !currentUser.admin) {
        next('/');
        return;
      } else if (currentUser.admin && !isAdminPage(to)) {
        next('/admin');
        return;
      }
    }

    if (to.matched.some(r => r.meta.mustBeSignedOut) && vuexStore.getters.isSignedIn) {
      next('/');
      return;
    }

    // ページ遷移時にスクロール状態が残る不具合への暫定対応
    // OPTIMIZE: https://github.com/agileware-jp/pest-vision/issues/229
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    next();
  });

  return router;
};

const router = buildRouter(store, {
  routes: [
    {
      path: '/sign_in',
      component: AppSignedOut,
      meta: { mustBeSignedOut: true },
      children: [
        {
          path: '',
          component: SignIn,
        },
        {
          path: '/register',
          component: SignUp,
        },
        {
          path: '/forgot_password',
          component: ForgotPassword,
        },
        {
          path: '/verify_otp',
          component: OtpVerification,
          props: route => ({ email: route.query.email }),
        },
        {
          path: 'universal_login',
          component: UniversalLogin,
        },
      ],
    },
    {
      path: '/admin',
      component: Admin.default,
      props: {
        admin: true,
      },
      meta: { authRequired: true, adminOnly: true },
      children: [
        {
          path: '',
          redirect: 'sensors',
        },
        {
          path: 'sensors',
          component: Admin.SensorList,
        },
        {
          path: 'sensors/:id',
          name: 'admin-sensor',
          component: Sensor,
          props: {
            admin: true,
          },
        },
        {
          path: 'accounts',
          component: Admin.AccountList,
        },
        {
          path: 'accounts/new',
          name: 'new-admin-account',
          component: UserForm,
          props: {
            admin: true,
            newRecord: true,
          },
        },
        {
          path: 'accounts/:email',
          name: 'admin-account',
          component: UserForm,
          props: {
            admin: true,
          },
        },
        {
          path: 'organizations',
          component: Admin.OrganizationList,
        },
        {
          path: 'organizations/new',
          name: 'new-admin-organization',
          component: Organization,
          props: {
            newRecord: true,
            admin: true,
          },
        },
        {
          path: 'organizations/:id',
          name: 'admin-organization',
          component: Organization,
          props: {
            admin: true,
          },
        },
        {
          path: 'activity_logs/:id',
          component: ActivityLogs,
          meta: { adminRoleOnly: true },
        },
        {
          path: 'activity_logs/:id/:date',
          component: ActivityLogs,
          meta: { adminRoleOnly: true },
        },
      ],
    },
    {
      path: '/',
      component: App,
      meta: { authRequired: true },
      children: [
        {
          path: '',
          redirect: 'activities',
        },
        {
          path: 'activities',
          component: Activity,
        },
        {
          path: 'sensors',
          component: SensorList,
        },
        {
          path: 'station',
          component: StationPlaces,
          meta: { requiresPermission: 'station' },
        },
        {
          path: 'station/:placeId',
          component: StationPlaceEvents,
          meta: { requiresPermission: 'station' },
          props: route => ({ placeId: route.params.placeId }),
        },
        {
          path: 'station/:placeId/recent',
          component: StationPlaceRecentEvents,
          meta: { requiresPermission: 'station' },
          props: route => ({ placeId: route.params.placeId }),
        },
        {
          path: 'station/:placeId/floor_plans',
          component: StationPlaceFloorPlans,
          meta: { requiresPermission: 'station' },
          props: route => ({ placeId: route.params.placeId }),
        },
        {
          path: 'sensors/new',
          component: Sensor,
          props: {
            new_record: true,
          },
        },
        {
          path: 'sensors/:id',
          name: 'sensor',
          component: Sensor,
        },
        {
          path: 'account',
          component: UserForm,
          meta: { openToDistributors: true },
        },
        {
          path: 'organization',
          component: Organization,
        },
        {
          path: 'information',
          component: InformationTab,
        },
        {
          path: 'mirakun_device',
          component: MirakunDevices,
        },
        {
          name: 'dashboard',
          path: 'dashboard',
          component: Dashboard,
        },
        {
          path: 'r_type_places',
          component: RTypePlaces,
          meta: { requiresPermission: ['rat_monitor', 'rat_sense'] },
        },
        {
          path: 'r_type_places/:placeId/timeline',
          component: RatTimeline,
          meta: { requiresPermission: ['rat_monitor', 'rat_sense'] },
          props: route => ({ placeId: route.params.placeId }),
        },
        {
          path: 'r_type_places/:placeId/detection_trend',
          component: RTypeDetectionTrend,
          meta: { requiresPermission: ['rat_monitor', 'rat_sense'] },
          props: route => ({ placeId: route.params.placeId }),
        },
        {
          path: 'r_type_places/:placeId/floor_plans',
          component: PlaceFloorPlans,
          meta: { requiresPermission: ['rat_monitor', 'rat_sense'] },
          props: route => ({ placeId: route.params.placeId }),
        },
      ],
    },
  ],
});

export default router;
